import { getApiClient } from '../api_client/get';
import { Container } from 'unstated';
import { getPersistentStore, getPersistentStoreMutex } from '@lemonadefashion/storage';

const KEY = 'i18n_settings_info_v4';
function _getStore() { return getPersistentStore(); }
function _getMutex() { return getPersistentStoreMutex(); }

function defaultI18nSettings() {
  return {
    currency: 'USD',
    locale: 'en-US',
    country: null,
    viewing_country: null,
    shipping_to: null,
    version: -1
  };
}

// Note that the keys i18n_settings and i18n_settings_timestamp
// may be polluted with garbage data since we used to use them
// before for storing the settings and timestamp separately.
// It is probably much much better to use a single key, because
// we can easily version it, and we can make the process of
// loading/setting data sort of atomic with respect to the
// needs of the container.

function getI18nSettingsInfoFromStore() {
  try {
    const i18nSettingsInfoJson = _getStore().getItem(KEY);
    if (i18nSettingsInfoJson == null) return { settings: defaultI18nSettings(), timestamp: 0 };
    return JSON.parse(i18nSettingsInfoJson);
  } catch (error) {
    console.log(error);
    return { settings: defaultI18nSettings(), timestamp: 0 };
  }
}

function setI18nSettingsInfoInStore(settings, timestamp) {
  return new Promise(function(resolve, reject) {
    const mutex = _getMutex();
    const store = _getStore();
    mutex.lock(KEY)
      .then(() => {
        store.setItem(KEY, JSON.stringify({ settings: settings, timestamp: timestamp }));
        return mutex.release(KEY).then(resolve).catch(reject);
      })
      .catch(reject);
  });
}

class I18nSettingsContainer extends Container {
  constructor() {
    super();
    this.state = { ...this._getUpdateFromStore(), ready: false, currencyChangeCounter: 0 };
    window.addEventListener('storage', e => {
      if (e.key !== KEY) return;
      this.setState(prevState => ({
        ...this._getUpdateFromStore(),
        currencyChangeCounter: prevState.currencyChangeCounter + 1
      }));
    });
  }

  // This must be called while the app is being initialized.
  initialize() {
    if (this.shouldGoToNetwork()) {
      return this.loadFromNetwork();
    } else {
      return new Promise((resolve, reject) => { this.setState({ ready: true }, resolve); });
    }
  }

  // Any I18n settings older than 1 hour are stale.
  shouldGoToNetwork() {
    if (this.state.settings == null) return true;
    if (this.state.timestamp == null) return true;
    const ageSeconds = (Date.now() - this.state.timestamp) / 1000;
    return ageSeconds > 3600;
  }

  isReady() {
    return this.state.ready;
  }

  _getUpdateFromStore() {
    const info = getI18nSettingsInfoFromStore();
    return { settings: info.settings, timestamp: info.timestamp };
  }

  _networkResolve(resolve, isCurrencyChange, response) {
    if (response.data.settings) {
      // We should only resolve it when we have network-storage-state consistency.
      // If writing to persistent storage fails, we should still update memory-state.
      setI18nSettingsInfoInStore(response.data.settings, Date.now())
        .catch(console.log)
        .finally(() => {
          this.setState(prevState =>
            ({
              ...this._getUpdateFromStore(),
              currencyChangeCounter: prevState.currencyChangeCounter + (isCurrencyChange ? 1 : 0),
              ready: true
            }),
            () => resolve(response));
        });
    } else {
      resolve(response);
    }
  }

  forceRefresh() {
    return this.loadFromNetwork();
  }

  loadFromNetwork() {
    return new Promise((resolve, reject) => {
      getApiClient().getI18nSettings()
        .then(this._networkResolve.bind(this, resolve, false))
        .catch(reject);
    });
  }

  getCurrency() { return this.state.settings.currency; }
  getLocale() { return this.state.settings.locale; }

  isLocaleRTL() {
    const locale = this.getLocale();
    return locale.startsWith('ar');
  }

  getViewerCountry() { return this.state.settings.country || this.state.settings.viewing_country; }
  getShippingCountry() { return this.state.settings.shipping_to; }

  // Whenever the currency is changed explicitly by the user, this counter increments.
  // This is useful for effects that need to refresh whenever a currency change happens.
  // This is more precise than using the currency value in a dependency list since the
  // currency can change from a fallback value to the real value, without requiring
  // an effect such as an item loading effect to change.
  getCurrencyChangeCounter() { return this.state.currencyChangeCounter; }

  setShippingCountry(country, isForced, cts) {
    return new Promise((resolve, reject) => {
      getApiClient().setShippingCountry(country, isForced, cts)
        .then(this._networkResolve.bind(this, resolve, false))
        .catch(reject);
    });
  }

  setCurrency(currency) {
    return new Promise((resolve, reject) => {
      getApiClient().setCurrency(currency)
        .then(this._networkResolve.bind(this, resolve, true))
        .catch(reject);
    });
  }

  setLocale(locale) {
    return new Promise((resolve, reject) => {
      getApiClient().setLocale(locale)
        .then(this._networkResolve.bind(this, resolve, true))
        .catch(reject);
    });
  }
}

export default I18nSettingsContainer;
